/* ============================================
   MYSTIC CARAT — theme.js v4
   ============================================ */
(function () {
  'use strict';
  const qs  = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => [...c.querySelectorAll(s)];

  /* ================================================  HEADER ---- */
  function initHeader() {
    const header = qs('#site-header');
    if (!header) return;
    window.addEventListener('scroll', () => header.classList.toggle('is-scrolled', window.scrollY > 40), { passive: true });
  }

  /* ================================================  SEARCH ---- */
  function initSearch() {
    const toggle      = qs('#search-toggle');
    const bar         = qs('#site-search-bar');
    const closeBtn    = qs('#search-close');
    const input       = qs('#search-input');
    const predictions = qs('#search-predictions');
    const predBody    = qs('#search-predictions-body');
    const viewAllLink = qs('#search-view-all');
    const queryLabel  = qs('#search-query-label');
    const form        = qs('#site-search-form');

    if (!toggle || !bar || !input) return;

    // ── Open / close ──────────────────────────────────────────
    let searchScrollY = 0;
    function openSearch() {
      bar.removeAttribute('hidden');
      bar.setAttribute('aria-hidden', 'false');
      input.focus();
      // iOS-safe: use body class (CSS handles position:fixed)
      searchScrollY = window.scrollY;
      document.body.style.top = `-${searchScrollY}px`;
      document.body.classList.add('nav-open');
    }
    function closeSearch() {
      bar.setAttribute('hidden', '');
      bar.setAttribute('aria-hidden', 'true');
      if (predictions) predictions.setAttribute('hidden', '');
      input.value = '';
      document.body.classList.remove('nav-open');
      document.body.style.top = '';
      window.scrollTo(0, searchScrollY);
    }

    toggle.addEventListener('click', openSearch);
    closeBtn?.addEventListener('click', closeSearch);
    // Click outside the search container closes it
    bar.addEventListener('click', e => { if (e.target === bar) closeSearch(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeSearch(); });

    // ── Predictive search via Shopify API ─────────────────────
    let debounceTimer;
    let lastQuery = '';
    const MIN_CHARS = 2;

    async function fetchPredictions(q) {
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor`;
        const res  = await fetch(url);
        if (!res.ok) throw new Error('Search API error');
        const data = await res.json();
        return data?.resources?.results?.products || [];
      } catch {
        return [];
      }
    }

    function renderPredictions(products, q) {
      if (!predBody || !predictions) return;

      if (products.length === 0) {
        predBody.innerHTML = `<p class="search-no-match">No products found for "<strong>${q}</strong>". Try a different term.</p>`;
      } else {
        predBody.innerHTML = products.map(p => {
          const img   = p.featured_image?.url
            ? `<img src="${p.featured_image.url}&width=80" alt="${p.title}" width="48" height="48" loading="lazy">`
            : `<div class="search-pred-img-placeholder"></div>`;
          const price = p.price ? `<span class="search-pred-price">₹${parseFloat(p.price).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>` : '';
          const highlight = p.title.replace(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'), '<mark>$1</mark>');
          return `<a class="search-pred-item" href="${p.url}" role="option">
            <div class="search-pred-img">${img}</div>
            <div class="search-pred-info">
              <span class="search-pred-title">${highlight}</span>
              <span class="search-pred-type">${p.product_type || ''}</span>
            </div>
            ${price}
          </a>`;
        }).join('');
      }

      if (viewAllLink && queryLabel) {
        viewAllLink.href = `/search?type=product&q=${encodeURIComponent(q)}`;
        queryLabel.textContent = q;
      }
      predictions.removeAttribute('hidden');
      input.setAttribute('aria-expanded', 'true');
    }

    function hidePredictions() {
      predictions?.setAttribute('hidden', '');
      input?.setAttribute('aria-expanded', 'false');
    }

    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(debounceTimer);
      if (q.length < MIN_CHARS) { hidePredictions(); lastQuery = ''; return; }
      if (q === lastQuery) return;
      lastQuery = q;
      debounceTimer = setTimeout(async () => {
        const products = await fetchPredictions(q);
        renderPredictions(products, q);
      }, 220);
    });

    // Keyboard nav through results
    input.addEventListener('keydown', e => {
      const items = qsa('.search-pred-item', predBody);
      if (!items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); items[0]?.focus(); }
    });
    predBody?.addEventListener('keydown', e => {
      const items = qsa('.search-pred-item', predBody);
      const idx   = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); items[idx + 1]?.focus() ?? input.focus(); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); idx > 0 ? items[idx - 1]?.focus() : input.focus(); }
      if (e.key === 'Escape')    { closeSearch(); }
    });

    // Ensure form submit goes to the right URL
    form?.addEventListener('submit', e => {
      const q = input.value.trim();
      if (!q) { e.preventDefault(); return; }
      // Let the native form submit handle the redirect
    });
  }

  /* ================================================  MOBILE NAV ---- */
  function initMobileNav() {
    const burger  = qs('#header-burger');
    const nav     = qs('#mobile-nav');
    const close   = qs('#mobile-nav-close');
    const overlay = qs('#mobile-overlay');
    if (!burger || !nav) return;

    // iOS-safe scroll lock: use position:fixed on body instead of overflow:hidden
    // overflow:hidden on body breaks position:fixed elements (nav drawer, search) on iOS Safari
    let scrollY = 0;

    const lockScroll = () => {
      scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add('nav-open');
    };
    const unlockScroll = () => {
      document.body.classList.remove('nav-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };

    const openNav = () => {
      nav.classList.add('is-open');
      nav.setAttribute('aria-hidden', 'false');
      overlay?.classList.add('is-visible');
      burger.setAttribute('aria-expanded', 'true');
      lockScroll();
    };
    const closeNav = () => {
      nav.classList.remove('is-open');
      nav.setAttribute('aria-hidden', 'true');
      overlay?.classList.remove('is-visible');
      burger.setAttribute('aria-expanded', 'false');
      unlockScroll();
    };

    // Helper: attach both click and touchend (iOS Safari sometimes suppresses click)
    const onTap = (el, fn) => {
      if (!el) return;
      el.addEventListener('click', fn);
      el.addEventListener('touchend', e => { e.preventDefault(); fn(); }, { passive: false });
    };

    onTap(burger, openNav);
    onTap(close, closeNav);
    if (overlay) onTap(overlay, closeNav);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeNav(); });

    // Mobile expand sub-menus
    qsa('[data-mobile-expand]').forEach(btn => {
      btn.addEventListener('click', () => {
        const sub = btn.nextElementSibling;
        if (!sub) return;
        const isOpen = !sub.hidden;
        sub.hidden = isOpen;
        btn.setAttribute('aria-expanded', String(!isOpen));
        btn.querySelector('svg')?.style.setProperty('transform', isOpen ? '' : 'rotate(180deg)');
      });
    });
  }

  /* ================================================  HERO SLIDESHOW — FIXED ---- */
  function initHeroSlideshow() {
    const slideshow = qs('#hero-slideshow');
    if (!slideshow) return;
    const slides   = qsa('.hero-img-slide', slideshow);
    const contents = qsa('.hero-slide-content');
    const dots     = qsa('#hero-dots .hero-dot');
    if (slides.length < 2) return;

    let current  = 0;
    let interval = parseInt(slideshow.dataset.interval) || 3000;
    const hero = slideshow.closest('.hero-banner');

    // Fix positioning
    slides.forEach(s => {
      s.style.position = 'absolute'; s.style.inset = '0';
      s.style.transition = 'opacity 1.2s ease'; s.style.zIndex = '0';
    });
    if (slides[0]) { slides[0].style.opacity = '1'; slides[0].style.zIndex = '1'; }

    function goTo(idx) {
      slides[current].style.opacity = '0'; slides[current].style.zIndex = '0';
      if (contents[current]) contents[current].classList.remove('is-active');
      dots[current]?.classList.remove('is-active');
      current = ((idx % slides.length) + slides.length) % slides.length;
      slides[current].style.opacity = '1'; slides[current].style.zIndex = '1';
      if (contents[current]) contents[current].classList.add('is-active');
      dots[current]?.classList.add('is-active');
    }

    dots.forEach((d, i) => d.addEventListener('click', () => { goTo(i); resetTimer(); }));
    let timer = setInterval(() => goTo(current + 1), interval);
    function resetTimer() { clearInterval(timer); timer = setInterval(() => goTo(current + 1), interval); }

    hero?.addEventListener('mouseenter', () => clearInterval(timer));
    hero?.addEventListener('mouseleave', resetTimer);
    let tx = 0;
    hero?.addEventListener('touchstart', e => { tx = e.touches[0].clientX; }, { passive: true });
    hero?.addEventListener('touchend', e => {
      const d = tx - e.changedTouches[0].clientX;
      if (Math.abs(d) > 40) { goTo(current + (d > 0 ? 1 : -1)); resetTimer(); }
    }, { passive: true });
  }

  /* ================================================  TRUST BADGE SCROLL ENTRANCE ---- */
  function initTrustBadgeAnimations() {
    const badges = qsa('.trust-badge');
    if (!badges.length || !('IntersectionObserver' in window)) {
      badges.forEach(b => b.classList.add('tb-visible'));
      return;
    }
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('tb-visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.15 });
    badges.forEach(b => obs.observe(b));
  }

  /* ================================================  PRODUCT GALLERY ---- */
  function initProductGallery() {
    const gallery  = qs('#product-gallery-main');
    const thumbsEl = qs('#product-thumbnails');
    if (!gallery || !thumbsEl) return;

    const slides    = qsa('.gallery-slide', gallery);
    const thumbBtns = qsa('.thumb', thumbsEl);
    let currentIdx  = 0;

    function goTo(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      currentIdx = i;
      slides.forEach((s, j) => s.classList.toggle('is-active', j === i));
      thumbBtns.forEach((t, j) => t.classList.toggle('is-active', j === i));
      // Scroll active thumb into view
      const activeThumb = thumbBtns[i];
      if (activeThumb) activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    thumbBtns.forEach((btn, i) => btn.addEventListener('click', () => goTo(i)));

    // ── Touch swipe on main gallery image ──
    let touchStartX = 0;
    let touchStartY = 0;
    gallery.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    gallery.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        if (dx < 0) goTo(currentIdx + 1); // swipe left → next
        else         goTo(currentIdx - 1); // swipe right → prev
      }
    }, { passive: true });

    // ── Thumbnail strip touch scroll (allow native scroll but also tap) ──
    let thumbTouchStartX = 0;
    let thumbMoved = false;
    thumbsEl.addEventListener('touchstart', e => {
      thumbTouchStartX = e.touches[0].clientX;
      thumbMoved = false;
    }, { passive: true });
    thumbsEl.addEventListener('touchmove', () => { thumbMoved = true; }, { passive: true });

    // ── Lightbox ──
    const lightbox    = qs('#gallery-lightbox');
    const lightboxImg = qs('#lightbox-img');
    const lightboxClose = qs('#lightbox-close');
    const lightboxPrev  = qs('#lightbox-prev');
    const lightboxNext  = qs('#lightbox-next');

    let lbScrollY = 0;
    function openLightbox(idx) {
      if (!lightbox || !lightboxImg) return;
      const slide = slides[idx];
      if (!slide) return;
      const img = slide.querySelector('img');
      if (!img) return;
      lightboxImg.src = img.dataset.full || img.src;
      lightboxImg.alt = img.alt;
      lightbox.classList.add('is-open');
      lbScrollY = window.scrollY;
      document.body.style.top = `-${lbScrollY}px`;
      document.body.classList.add('nav-open');
    }
    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      document.body.classList.remove('nav-open');
      document.body.style.top = '';
      window.scrollTo(0, lbScrollY);
    }
    function lightboxGoTo(idx) {
      idx = Math.max(0, Math.min(slides.length - 1, idx));
      goTo(idx);
      const slide = slides[idx];
      if (!slide) return;
      const img = slide.querySelector('img');
      if (img && lightboxImg) {
        lightboxImg.src = img.dataset.full || img.src;
        lightboxImg.alt = img.alt;
      }
    }

    // Click main image → open lightbox
    gallery.addEventListener('click', () => openLightbox(currentIdx));

    if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
    if (lightbox)      lightbox.addEventListener('click', e => { if (e.target === lightbox) closeLightbox(); });
    if (lightboxPrev)  lightboxPrev.addEventListener('click', e => { e.stopPropagation(); lightboxGoTo(currentIdx - 1); });
    if (lightboxNext)  lightboxNext.addEventListener('click', e => { e.stopPropagation(); lightboxGoTo(currentIdx + 1); });

    // Keyboard navigation in lightbox
    document.addEventListener('keydown', e => {
      if (!lightbox || !lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape')      closeLightbox();
      if (e.key === 'ArrowLeft')   lightboxGoTo(currentIdx - 1);
      if (e.key === 'ArrowRight')  lightboxGoTo(currentIdx + 1);
    });

    // Lightbox swipe on mobile
    let lbTouchX = 0;
    if (lightbox) {
      lightbox.addEventListener('touchstart', e => { lbTouchX = e.touches[0].clientX; }, { passive: true });
      lightbox.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - lbTouchX;
        if (Math.abs(dx) > 50) {
          if (dx < 0) lightboxGoTo(currentIdx + 1);
          else         lightboxGoTo(currentIdx - 1);
        }
      }, { passive: true });
    }
  }

  /* ================================================  VARIANT SELECTOR ---- */
  function initVariantSelector() {
    const form = qs('#product-form');
    if (!form || !window.productData) return;
    const variants = window.productData;
    const variantInput = qs('#variant-id', form);
    const priceEl = qs('#product-price');
    const addBtn  = qs('#add-to-cart');
    const skuEl   = qs('#product-sku');
    const availEl = qs('#product-availability');
    const sel = {};

    qsa('.product-option', form).forEach(opt => {
      const active = qs('.variant-pill.is-active, .swatch.is-active', opt);
      if (active) sel[active.dataset.optionIndex] = active.dataset.value;
    });

    const findVariant = () => variants.find(v => v.options.every((o, i) => sel[i] === o));
    const fmt = c => '₹' + (c/100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    /* ------- Gallery image switcher ------- */
    function switchGalleryToImage(variant) {
      if (!variant) return;
      const gallery   = qs('#product-gallery-main');
      const thumbsEl  = qs('#product-thumbnails');
      if (!gallery) return;

      const slides    = qsa('.gallery-slide', gallery);
      const thumbBtns = thumbsEl ? qsa('.thumb', thumbsEl) : [];

      let targetIdx = -1;

      /* 1. Use variant's featured_image id to find matching slide by data-image-id */
      if (variant.featured_image && variant.featured_image.id) {
        const imgId = String(variant.featured_image.id);
        slides.forEach((slide, i) => {
          if (slide.dataset.imageId === imgId && targetIdx === -1) targetIdx = i;
        });
      }

      /* 2. Fallback: match by alt text containing any selected option value */
      if (targetIdx === -1) {
        const optionValues = Object.values(sel).map(v => v.toLowerCase());
        slides.forEach((slide, i) => {
          const img = slide.querySelector('img');
          if (!img) return;
          const alt = (img.getAttribute('alt') || '').toLowerCase();
          if (optionValues.some(val => alt.includes(val)) && targetIdx === -1) {
            targetIdx = i;
          }
        });
      }

      if (targetIdx >= 0 && targetIdx < slides.length) {
        slides.forEach((s, i) => s.classList.toggle('is-active', i === targetIdx));
        thumbBtns.forEach((t, i) => t.classList.toggle('is-active', i === targetIdx));
      }
    }

    function updateUI(v) {
      if (!v) return;
      variantInput.value = v.id;
      if (priceEl) priceEl.innerHTML = (v.compare_at_price > v.price
        ? `<span class="price--sale">${fmt(v.price)}</span><span class="price--compare">${fmt(v.compare_at_price)}</span>`
        : `<span class="price--regular">${fmt(v.price)}</span>`) + `<span class="price-note">Incl. of all taxes</span>`;
      if (addBtn) { addBtn.disabled = !v.available; addBtn.textContent = v.available ? 'Add to Cart' : 'Sold Out'; }
      if (skuEl) skuEl.textContent = v.sku || '';
      if (availEl) availEl.textContent = v.available ? 'In Stock' : 'Sold Out';
      switchGalleryToImage(v);
    }

    qsa('.variant-pill, .swatch', form).forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = btn.dataset.optionIndex;
        sel[idx] = btn.dataset.value;
        qsa('.variant-pill, .swatch', btn.closest('.product-option__values')).forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        const lbl = qs(`#option-${parseInt(idx)+1}-label`);
        if (lbl) lbl.textContent = btn.dataset.value;
        updateUI(findVariant());
      });
    });
    updateUI(findVariant());
  }

  /* ================================================  QUANTITY ---- */
  function initQty() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('.qty-btn');
      if (!btn) return;
      const input = btn.closest('.quantity-selector')?.querySelector('.qty-input');
      if (!input) return;
      const key = btn.dataset.key;
      let val = parseInt(input.value) || 1;
      val += btn.dataset.action === 'increase' ? 1 : -1;
      val = Math.max(key ? 0 : 1, val);
      input.value = val;
      if (key) updateCartItem(key, val);
    });
  }

  /* ================================================  ADD TO CART ---- */
  function initAddToCart() {
    const form = qs('#product-form');
    if (!form) return;

    // Add to Cart — AJAX
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const btn = qs('#add-to-cart', form);
      if (!btn || btn.disabled) return;
      const fd = new FormData(form);
      btn.textContent = 'Adding…'; btn.disabled = true;
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: fd.get('id'), quantity: parseInt(fd.get('quantity') || 1) })
        });
        if (!r.ok) throw new Error();
        await updateCartBadge();
        showToast('Added to cart! 🛒');
      } catch {
        showToast('Something went wrong. Please try again.', 'error');
      } finally {
        btn.textContent = 'Add to Cart';
        btn.disabled = false;
      }
    });

    // Buy Now — add to cart then redirect to checkout via a hidden form POST
    // (window.location.href = '/checkout' is blocked on some Shopify stores)
    qs('#buy-now')?.addEventListener('click', async () => {
      const id  = qs('#variant-id')?.value;
      const qty = parseInt(qs('#quantity')?.value || 1);
      if (!id) return;

      const btn = qs('#buy-now');
      const origText = btn.textContent;
      btn.textContent = 'Processing…';
      btn.disabled = true;

      try {
        // 1. Clear cart and add just this item
        await fetch('/cart/clear.js', { method: 'POST' });
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, quantity: qty })
        });
        if (!r.ok) throw new Error('Failed to add item');

        // 2. Submit via a hidden form — this is the only method Shopify guarantees
        const checkoutForm = document.createElement('form');
        checkoutForm.method = 'POST';
        checkoutForm.action = '/checkout';
        document.body.appendChild(checkoutForm);
        checkoutForm.submit();

      } catch (err) {
        showToast('Could not process. Please add to cart and checkout.', 'error');
        btn.textContent = origText;
        btn.disabled = false;
      }
    });
  }

  async function updateCartItem(key, qty) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      if (!res.ok) throw new Error('Cart update failed');
      const cart = await res.json();

      // Remove item row if qty = 0
      if (qty === 0) {
        qs(`[data-key="${key}"]`)?.remove();
      }

      // Update each line item price in the DOM
      cart.items.forEach(item => {
        const row = qs(`[data-key="${item.key}"]`);
        if (!row) return;
        // Update line price span (has its own data-key too)
        const priceEl = qs(`.cart-item__price[data-key="${item.key}"]`);
        if (priceEl) priceEl.textContent = formatMoney(item.final_line_price);
        // Sync qty input in case server capped it
        const qtyInput = row.querySelector('.qty-input');
        if (qtyInput) qtyInput.value = item.quantity;
      });

      // Update order summary totals using reliable IDs
      const subtotalPriceEl = qs('#cart-subtotal-price');
      if (subtotalPriceEl) subtotalPriceEl.textContent = formatMoney(cart.total_price);

      const totalEl = qs('#cart-total-price');
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

      const subtotalLabel = qs('#cart-subtotal-label');
      if (subtotalLabel) subtotalLabel.textContent = `Subtotal (${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})`;

      // If cart is now empty, reload to show empty state
      if (cart.item_count === 0) {
        window.location.reload();
        return;
      }

      // Update cart badge in header
      await updateCartBadge();

    } catch (err) {
      console.error('Cart update error:', err);
      window.location.reload(); // fallback: reload page to show accurate cart
    }
  }

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const [int, dec] = amount.split('.');
    const formatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `₹${formatted}.${dec}`;
  }
  function initCartRemovals() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('.cart-item__remove');
      if (btn?.dataset.key) await updateCartItem(btn.dataset.key, 0);
    });
  }
  async function updateCartBadge() {
    try { const c = await (await fetch('/cart.js')).json(); const b = qs('.cart-badge'); if(b){b.textContent=c.item_count;b.style.display=c.item_count>0?'flex':'none';} } catch {}
  }

  function initSort() {
    const s = qs('#sort-by');
    if (!s) return;
    s.addEventListener('change', () => { const u=new URL(location.href); u.searchParams.set('sort_by',s.value); location.href=u; });
  }

  function showToast(msg, type='success') {
    let c = qs('#toast-container');
    if (!c) { c=document.createElement('div'); c.id='toast-container'; c.style.cssText='position:fixed;bottom:24px;right:24px;z-index:9999;display:flex;flex-direction:column;gap:8px;'; document.body.appendChild(c); }
    const t = document.createElement('div');
    t.style.cssText = `padding:12px 20px;background:${type==='error'?'#c0392b':'#1a6858'};color:#fff;border-radius:4px;font-family:'Jost',sans-serif;font-size:.875rem;box-shadow:0 4px 16px rgba(0,0,0,.2);transform:translateX(120%);transition:transform 300ms ease;`;
    t.textContent = msg; c.appendChild(t);
    requestAnimationFrame(() => requestAnimationFrame(() => { t.style.transform='translateX(0)'; }));
    setTimeout(() => { t.style.transform='translateX(120%)'; setTimeout(()=>t.remove(),320); }, 3000);
  }

  /* ================================================  OTP POPUP ---- */
  function initOTPPopup() {
    const overlay = qs('#mc-popup-overlay');
    if (!overlay) return;

    const COOKIE = 'mc_otp_done';
    const SESSION = 'mc_popup_shown';
    if (document.cookie.includes(COOKIE+'=')) return;
    if (sessionStorage.getItem(SESSION)) return;

    let popupScrollY = 0;
    function openPopup() {
      overlay.removeAttribute('hidden');
      requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.add('is-visible')));
      sessionStorage.setItem(SESSION, '1');
      popupScrollY = window.scrollY;
      document.body.style.top = `-${popupScrollY}px`;
      document.body.classList.add('nav-open');
    }
    function closePopup() {
      overlay.classList.remove('is-visible');
      setTimeout(() => {
        overlay.setAttribute('hidden', '');
        document.body.classList.remove('nav-open');
        document.body.style.top = '';
        window.scrollTo(0, popupScrollY);
      }, 350);
    }

    setTimeout(openPopup, 10000);
    overlay.addEventListener('click', e => { if (e.target === overlay) closePopup(); });
    qs('#mc-popup-close')?.addEventListener('click', closePopup);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closePopup(); });

    /* --- Field references --- */
    const firstName = qs('#mc-first-name');
    const lastName  = qs('#mc-last-name');
    const phone     = qs('#mc-phone');
    const step1     = qs('#mc-step-1');
    const step2     = qs('#mc-step-2');
    const step3     = qs('#mc-step-3');
    const sendOtpBtn   = qs('#mc-send-otp');
    const verifyOtpBtn = qs('#mc-verify-otp');
    const otpDisplay   = qs('#mc-otp-phone-display');
    const otpDigits    = qsa('.mc-otp-digit');
    const errFirst     = qs('#err-first');
    const errLast      = qs('#err-last');
    const errPhone     = qs('#err-phone');
    const errOtp       = qs('#err-otp');
    const resendBtn    = qs('#mc-resend');
    const backBtn      = qs('#mc-back');
    const successMsg   = qs('#mc-success-msg');
    const successSub   = qs('#mc-success-sub');

    /* --- Validation helpers --- */
    const hasNum = s => /\d/.test(s);
    const isValidIndianPhone = s => /^[6-9]\d{9}$/.test(s);

    function clearErr(el) { if(el) { el.textContent=''; el.previousElementSibling?.classList.remove('is-error'); } }
    function setErr(el, input, msg) { if(el){ el.textContent=msg; } input?.classList.add('is-error'); }

    /* --- Only letters in name fields --- */
    [firstName, lastName].forEach(inp => {
      inp?.addEventListener('input', () => {
        inp.value = inp.value.replace(/[^a-zA-Z\s]/g, '');
      });
    });

    /* --- Only digits in phone --- */
    phone?.addEventListener('input', () => {
      phone.value = phone.value.replace(/\D/g, '').slice(0, 10);
    });

    /* --- Send OTP --- */
    sendOtpBtn?.addEventListener('click', async () => {
      let valid = true;
      [errFirst, errLast, errPhone].forEach(e => clearErr(e));
      [firstName, lastName, phone].forEach(i => i?.classList.remove('is-error'));

      const fn = firstName?.value.trim() || '';
      const ln = lastName?.value.trim() || '';
      const ph = phone?.value.trim() || '';

      if (!fn) { setErr(errFirst, firstName, 'First name is required.'); valid=false; }
      else if (hasNum(fn)) { setErr(errFirst, firstName, 'First name must not contain numbers.'); valid=false; }

      if (!ln) { setErr(errLast, lastName, 'Last name is required.'); valid=false; }
      else if (hasNum(ln)) { setErr(errLast, lastName, 'Last name must not contain numbers.'); valid=false; }

      if (!ph) { setErr(errPhone, phone, 'Phone number is required.'); valid=false; }
      else if (!isValidIndianPhone(ph)) { setErr(errPhone, phone, 'Enter a valid 10-digit Indian mobile number (starts with 6–9).'); valid=false; }

      if (!valid) return;

      sendOtpBtn.textContent = 'Sending…'; sendOtpBtn.disabled = true;

      /* ---- OTP send — integrate your SMS provider (MSG91/Twilio) here ----
         Example for MSG91:
         await fetch('https://api.msg91.com/api/v5/otp?template_id=YOUR_ID&mobile=91'+ph, { method:'POST', ... });
         For now, we simulate and show a console note.
      */
      console.info('[Mystic Carat] OTP requested for +91' + ph + '. Connect MSG91 / Twilio in your backend to send real SMS.');

      // Simulate network delay
      await new Promise(r => setTimeout(r, 800));

      if (otpDisplay) otpDisplay.textContent = '+91 ' + ph;
      step1.hidden = true; step2.removeAttribute('hidden');
      otpDigits[0]?.focus();
      sendOtpBtn.textContent = 'SEND OTP'; sendOtpBtn.disabled = false;
      startResendTimer(30);
    });

    /* --- OTP digit auto-advance --- */
    otpDigits.forEach((digit, i) => {
      digit.addEventListener('keydown', e => {
        if (e.key === 'Backspace' && !digit.value && i > 0) otpDigits[i-1]?.focus();
      });
      digit.addEventListener('input', () => {
        digit.value = digit.value.replace(/\D/g,'').slice(-1);
        if (digit.value) { digit.classList.add('is-filled'); if (i < otpDigits.length-1) otpDigits[i+1]?.focus(); }
        else digit.classList.remove('is-filled');
      });
      digit.addEventListener('paste', e => {
        e.preventDefault();
        const text = (e.clipboardData||window.clipboardData).getData('text').replace(/\D/g,'').slice(0,6);
        text.split('').forEach((ch, j) => { if (otpDigits[j]) { otpDigits[j].value=ch; otpDigits[j].classList.add('is-filled'); } });
        otpDigits[Math.min(text.length, otpDigits.length-1)]?.focus();
      });
    });

    /* --- Verify OTP --- */
    verifyOtpBtn?.addEventListener('click', async () => {
      errOtp && (errOtp.textContent = '');
      const enteredOtp = otpDigits.map(d => d.value).join('');
      if (enteredOtp.length < 6) { if(errOtp) errOtp.textContent='Please enter the complete 6-digit OTP.'; return; }

      verifyOtpBtn.textContent = 'Verifying…'; verifyOtpBtn.disabled = true;

      /* ---- OTP verify — call your backend / MSG91 verify endpoint here ----
         const res = await fetch('/api/otp-verify', { method:'POST', body: JSON.stringify({ phone, otp: enteredOtp }) });
         const { status, isNewUser } = await res.json();
         For now, we simulate success.
      */
      await new Promise(r => setTimeout(r, 1000));
      const isNewUser = true; // Replace with actual API response

      step2.hidden = true; step3.removeAttribute('hidden');
      if (isNewUser) {
        if(successMsg) successMsg.textContent = 'Welcome to Mystic Carat, ' + (firstName?.value.trim() || '') + '! 🎉';
        if(successSub) successSub.textContent = 'Your account has been created. Start exploring our collections!';
      } else {
        if(successMsg) successMsg.textContent = 'Welcome back, ' + (firstName?.value.trim() || '') + '! 👋';
        if(successSub) successSub.textContent = "You're logged in. Explore what's new at Mystic Carat.";
      }

      // Set 30-day cookie
      const exp = new Date(); exp.setDate(exp.getDate()+30);
      document.cookie = `${COOKIE}=1; expires=${exp.toUTCString()}; path=/; SameSite=Lax`;
      verifyOtpBtn.textContent = 'VERIFY & CONTINUE'; verifyOtpBtn.disabled = false;
    });

    /* --- Resend timer --- */
    let resendInterval;
    function startResendTimer(sec) {
      if (resendBtn) resendBtn.disabled = true;
      const timerEl = qs('#mc-resend-timer');
      let s = sec;
      if (timerEl) timerEl.textContent = `(${s}s)`;
      resendInterval = setInterval(() => {
        s--;
        if (timerEl) timerEl.textContent = s > 0 ? `(${s}s)` : '';
        if (s <= 0) { clearInterval(resendInterval); if(resendBtn) resendBtn.disabled=false; }
      }, 1000);
    }
    resendBtn?.addEventListener('click', async () => {
      otpDigits.forEach(d => { d.value=''; d.classList.remove('is-filled'); });
      if(errOtp) errOtp.textContent = '';
      resendBtn.disabled = true;
      await new Promise(r => setTimeout(r, 600));
      startResendTimer(30);
      console.info('[Mystic Carat] OTP resent.');
    });

    /* --- Back button --- */
    backBtn?.addEventListener('click', () => {
      step2.hidden = true; step1.removeAttribute('hidden');
      otpDigits.forEach(d => { d.value=''; d.classList.remove('is-filled'); });
      if(errOtp) errOtp.textContent = '';
      clearInterval(resendInterval);
    });
  }

  /* ================================================  NA TABS ---- */
  function initNewArrivalsTabs() {
    const tabs  = qsa('.na-tab');
    const grid  = qs('#na-grid-static');
    const ctaBtn = qs('#na-cta-btn');

    if (!tabs.length) return;

    // If there's no static grid (using live collection), tabs just style themselves
    if (!grid) {
      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          tabs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');
        });
      });
      return;
    }

    function filterCards(activeTabId) {
      const cards = qsa('.na-card', grid);
      let visibleCount = 0;

      cards.forEach(card => {
        // Treat blank / missing category as "all" (always visible)
        const raw = (card.dataset.category || '').trim().toLowerCase();
        const cardCategory = raw === '' ? 'all' : raw;
        const tabId = activeTabId.trim().toLowerCase();

        // Show if:
        //   • active tab is "all"  → show everything
        //   • card's category matches the tab id exactly
        //   • card's category is "all" → always show regardless of active tab
        const shouldShow = tabId === 'all' || cardCategory === tabId || cardCategory === 'all';

        card.classList.toggle('na-card--hidden', !shouldShow);
        if (shouldShow) visibleCount++;
      });

      // Empty-state message
      const existing = qs('.na-empty', grid);
      if (existing) existing.remove();
      if (visibleCount === 0) {
        const empty = document.createElement('p');
        empty.className = 'na-empty';
        empty.textContent = 'No products in this category yet — check back soon!';
        grid.appendChild(empty);
      }
    }

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        // Update active state
        tabs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');

        // Filter the cards
        filterCards(tab.dataset.tab || 'all');
      });
    });

    // Run filter on load to match the default active tab
    const defaultTab = tabs.find(t => t.classList.contains('is-active')) || tabs[0];
    if (defaultTab) filterCards(defaultTab.dataset.tab || 'all');
  }

  /* ================================================  SCROLL REVEAL ---- */
  function initScrollReveal() {
    if (!('IntersectionObserver' in window)) return;
    const targets = qsa('.product-card, .testimonial-card, .category-card, .hc-photo, .ig-post, .sbv-card, .blog-card');
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if(e.isIntersecting){ e.target.style.animationPlayState='running'; obs.unobserve(e.target); } });
    }, { threshold:0.08 });
    targets.forEach(el => { el.style.animationPlayState='paused'; obs.observe(el); });
  }

  /* ================================================
     SCROLL TO TOP
     ================================================ */
  function initScrollToTop() {
    const btn = qs('#scroll-to-top');
    if (!btn) return;

    // Show button after scrolling 400px down
    const onScroll = () => {
      btn.classList.toggle('is-visible', window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // run once on load

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ================================================  INIT ---- */
  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initSearch();
    initMobileNav();
    initHeroSlideshow();
    initTrustBadgeAnimations();
    initProductGallery();
    initVariantSelector();
    initQty();
    initAddToCart();
    initCartRemovals();
    initSort();
    initOTPPopup();
    initNewArrivalsTabs();
    initScrollReveal();
    initScrollToTop();
  });
})();
